import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import gsap from "gsap";

function Home() {
  useEffect(() => {
    // HOME PAGE JS
    function updateOffset() {
      const container = document.querySelector('.container');
      if (!container) return;
      const rect = container.getBoundingClientRect();

      const style = getComputedStyle(container);
      const paddingLeft = parseFloat(style.paddingLeft);

      document.documentElement.style.setProperty(
        '--container-offset',
        `${rect.left + paddingLeft}px`
      );
    }

    updateOffset();
    window.addEventListener('resize', updateOffset);

    const cleanupFns = [];

    const carousels = document.querySelectorAll('.js-carousel');
    carousels.forEach((carousel) => {
      const slider = carousel.querySelector('.carousel__slider');
      const items = carousel.querySelectorAll('.carousel__item');
      const prev = carousel.querySelector('.js-carousel-prev');
      const next = carousel.querySelector('.js-carousel-next');

      if (!slider || !prev || !next || items.length === 0) return;

      let index = 0;
      let slideWidth = 0;
      let slidesPerView = 2;
      let maxIndex = 0;

      function updateMetrics() {
        const gap = parseFloat(getComputedStyle(slider).gap) || 0;
        slideWidth = items[0].getBoundingClientRect().width + gap;

        slidesPerView = window.innerWidth < 768 ? 1 : 2;
        maxIndex = items.length - slidesPerView;

        // Clamp index on resize
        index = Math.min(index, maxIndex);
        move(false);
      }

      function move(animate = true) {
        gsap.to(slider, {
          x: -(index * slideWidth),
          duration: animate ? 0.6 : 0,
          ease: 'power3.inOut'
        });
      }

      const nextClick = () => {
        if (index < maxIndex) {
          index++;
          move();
        }
      };

      const prevClick = () => {
        if (index > 0) {
          index--;
          move();
        }
      };

      next.addEventListener('click', nextClick);
      prev.addEventListener('click', prevClick);

      updateMetrics();
      window.addEventListener('resize', updateMetrics);

      cleanupFns.push(() => {
        window.removeEventListener('resize', updateMetrics);
        next.removeEventListener('click', nextClick);
        prev.removeEventListener('click', prevClick);
      });
    });

    return () => {
      window.removeEventListener('resize', updateOffset);
      cleanupFns.forEach(fn => fn());
    };
  }, []);

  return (
    <main className="main">
      <section className="section-01">
        <div className="wrapper">
          <div className="contents">
            <img src={require("../assets/images/logo.png")} className="ikigai-logo" width="192" alt="logo" />
            <p className="tagline">Your <span>Journey</span> to Feeling <span>Alive</span> <br /> and <span>Fulfilled
              Everyday</span></p>
          </div>
          <div className="_right">
            <img src={require("../assets/images/Ikigai.png")} className="frame" alt="frame" />
          </div>
          <div className="_left">
            <div className="content">
              <h2 className="subheading">Are you ready to experience something unexpected?</h2>
              <p className="text">A combination of learning with logic and feeling the magic of Ikigai journeys
                awaits you.</p>
              <p className="text"><span>Ikigai</span> means <span>Life Worth Living</span></p>
              <p className="text">It’s not a destination — it’s found in the small, everyday moments that make us
                feel alive and fulfilled. Ikigai365 takes this centuries-old Japanese way of life to try and
                help humankind ‘make life worth living everyday’.</p>
              <p className="text">Through real stories from Japan and reflection tools, This is Ikigai365 helps
                you embrace your own rhythm <span>— your own pace.</span></p>
              <p className="text"><span>Turn everyday moments into beautiful reminders to feel alive and
                fulfilled,</span> no matter what season of life you are in.</p>
              <p className="text">Because your Ikigai365 journey begins here — in this very moment.</p>
              <a href="https://www.penguin.sg/book/this-is-ikigai365" target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                <span className="material-symbols-outlined">
                  barefoot
                </span>Begin your Journey
              </a>
            </div>
          </div>
        </div>
      </section>
      <section className="section-02">
        <div className="container-fluid">
          <div className="wrapper">
            <div className="_content">
              <span className="date">June 2025 | Dharamshala, India</span>
              <h3 className="_heading">With Blessings from His Holiness<span className="_subtext">The Dalai
                Lama</span>
              </h3>
              <p className="_text">“The purpose and meaning of our life is to be happy and true happiness results
                from a peace of mind underpinned by a compassionate heart. This is most important for a
                fulfilled life. </p>
              <p className="_text">I hope the readers of this book will find it helpful in bringing about a better
                altruistic way of life.”
              </p>
              <p className="holiness">— His Holiness The Dalai Lama</p>
              <a
                href={require("../assets/images/HHDL-foreword.pdf")}
                target="_blank" rel="noreferrer"
                className="btn btn-primary">
                <span className="material-icons-outlined">
                  draw
                </span>Read the complete Foreword
              </a>
            </div>
            <div className="_photograph">
              <img src={require("../assets/images/forward.jpg")} alt="forward" className="_team" />
            </div>
          </div>
        </div>
      </section>
      <section className="section-03">
        <div className="container-fluid">
          <div className="_content">
            <h3 className="_heading">Through the Seasons of Life — Stories from Japan</h3>
            <p className="_text">Step into the lives of <span>everyday people</span> who remind us that
              <span>every moment</span> holds a chance to feel alive and fulfilled — no matter the season
              you’re in.
            </p>
          </div>
        </div>
        <div className="container">
          <div className="carousel js-carousel">
            <div className="navigator">
              <button className="js-carousel-prev" type="button"><span className="material-icons-outlined">
                call_made
              </span></button>
              <button className="js-carousel-next" type="button"><span className="material-icons-outlined">
                call_made
              </span></button>
            </div>

            <div className="carousel__slider">
              <div className="carousel__item">
                <div className="profiles">
                  <div className="image">
                    <img src={require("../assets/images/2.png")} className="frame" alt="hero" />
                  </div>
                  <div className="content">
                    <h4 className="heading">Life Worth ≠ Net Worth</h4>
                    <p className="text">‘A small boy once wished to heal the planet. Decades later, his
                      moments ripples across 120+ countries.’</p>
                    <p className="text">Through every idea and act of care, he lives by his belief that
                      <span>‘small happiness leads to big happiness in life.’</span>
                    </p>
                    <p className="headline"><span>Step into</span> Fujio-san’s spring</p>
                    <Link to="/stories" className="btn btn-primary">
                      READ MORE
                      <img src={require("../assets/images/arrow.png")} className="arrow" width="16" alt="arrow" />
                    </Link>
                  </div>
                </div>
              </div>
              <div className="carousel__item">
                <div className="profiles">
                  <div className="image">
                    <img src={require("../assets/images/3.png")} className="frame" alt="hero" />
                  </div>
                  <div className="content">
                    <h4 className="heading">Creating My Own Ibasho - A Place to Belong</h4>
                    <p className="text">From a busy mother’s kitchen to a community of light - Hanazawa-san
                      turns everyday life into celebration</p>
                    <p className="text">Her story reminds us that <span>when we open our doors, life opens
                      its heart back to us.</span></p>
                    <p className="headline"><span>Step into</span> Hanazawa-san’s Ibasho</p>
                    <Link to="/stories" className="btn btn-primary">
                      READ MORE
                      <img src={require("../assets/images/arrow.png")} className="arrow" width="16" alt="arrow" />
                    </Link>
                  </div>
                </div>
              </div>
              <div className="carousel__item">
                <div className="profiles">
                  <div className="image">
                    <img src={require("../assets/images/4.png")} className="frame" alt="hero" />
                  </div>
                  <div className="content">
                    <h4 className="heading">Rewire – Don’t Retire</h4>
                    <p className="text">Ishizaki-san lives by a simple rhythm <span>— give, give, give, and
                      life gives back in ways you never expect.</span></p>
                    <p className="text">Through every connection and act of generosity, he keeps the
                      <span>spirit of feeling alive and fulfilled flowing.</span>
                    </p>
                    <p className="headline"><span>Step into</span> Ishizaki-san’s world</p>
                    <Link to="/stories" className="btn btn-primary">
                      READ MORE
                      <img src={require("../assets/images/arrow.png")} className="arrow" width="16" alt="arrow" />
                    </Link>
                  </div>
                </div>
              </div>
              <div className="carousel__item">
                <div className="profiles">
                  <div className="image">
                    <img src={require("../assets/images/5.png")} className="frame" alt="hero" />
                  </div>
                  <div className="content">
                    <h4 className="heading">Curiosity is Ageless</h4>
                    <p className="text">Ogawa-san reminds us that <span>no matter when we are in life,
                      curiosity continues to glow.</span></p>
                    <p className="text">She continues to learn, create, and give — proving that a curious
                      mind keeps the heart forever young.</p>
                    <p className="headline"><span>Step into</span> How curiosity keeps Ogawa-san going</p>
                    <Link to="/stories" className="btn btn-primary">
                      READ MORE
                      <img src={require("../assets/images/arrow.png")} className="arrow" width="16" alt="arrow" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="section-04">
        <div className="container-fluid">
          <div className="wrapper">
            <h3 className="_heading">The collaborative magic that birthed <span className="_subtext">Ikigai365</span>
            </h3>
            <div className="_content">
              <p className="_text"><span>Ikigai365</span> was born when five individuals from Japan, Singapore,
                and India came together—each from a different walk of life, yet united by one shared
                mission: to spread an authentic understanding of Ikigai and a belief that <span>everyone
                  deserves a life worth living, every day.</span></p>
              <p className="_text">Our hope is that this book inspires you to begin your own journey — one that
                <span> feels alive, fulfilled, and truly your own.</span>
              </p>
              <Link to="/about" className="btn btn-primary">
                <span className="material-icons-outlined">
                  read_more
                </span>Know More
              </Link>
            </div>
            <div className="_photograph">
              <img src={require("../assets/images/team.png")} alt="team" className="_team" />
              <p className="_text">Shibuya Sky, Tokyo — June 2023.<br /> The Ikigai365 team’s first in-person
                workshop.</p>
            </div>
          </div>
        </div>
      </section>

    </main>
  );
}

export default Home;

